import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { secureStorage } from '../utils/secureStorage';

// Prevent browsers from sending cookies on cross-origin API requests
// This avoids CORS failures when Cloudflare/proxy sets ACAO:* with ACAC:true
axios.defaults.withCredentials = false;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(secureStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(
    secureStorage.getItem('forcePasswordChange') === 'true'
  );

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Global "signed in on another device" interceptor.
  // When the backend rotates `active_session_id` (i.e. user logged in on
  // another device), our existing token becomes invalid and the API returns
  // 401 with X-Session-Replaced: true. We log the user out gracefully.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        const replaced =
          err?.response?.headers?.['x-session-replaced'] === 'true' ||
          err?.response?.headers?.['X-Session-Replaced'] === 'true';
        if (status === 401 && replaced) {
          secureStorage.removeItem('token');
          secureStorage.removeItem('is_first_login');
          secureStorage.removeItem('forcePasswordChange');
          setToken(null);
          setUser(null);
          try {
            toast.error('You signed in on another device. This session has been ended.', {
              duration: 6000,
              id: 'session-replaced',
            });
          } catch { /* noop */ }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        // Retry logic for transient failures
        let lastError = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const response = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10000,
              withCredentials: false
            });
            setUser(response.data);
            if (secureStorage.getItem('is_first_login') === 'true') {
              setIsFirstLogin(true);
            }
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            // Only clear token on 401 (invalid/expired token)
            if (error.response?.status === 401) {
              secureStorage.removeItem('token');
              secureStorage.removeItem('is_first_login');
              setToken(null);
              setUser(null);
              lastError = null;
              break;
            }
            // For network errors, 500s, timeouts — wait and retry
            if (attempt < 1) {
              await new Promise(r => setTimeout(r, 2000));
            }
          }
        }
        // If all retries failed with non-401 errors, keep the token
        // so the user isn't logged out due to temporary server issues
        if (lastError) {
          console.warn('Auth check failed (server may be temporarily unavailable), keeping session');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, API]);

  // iter_436: keep the user payload fresh so admin role changes
  // (promote/demote/grant VIP/PTL etc.) propagate WITHOUT requiring a
  // logout. Refreshes:
  //   • every 30s while the tab is open + the user is logged in
  //   • immediately when the tab regains visibility (focus event)
  // Backend admin role-mutation endpoints already invalidate the user
  // cache server-side; this just pulls the new payload client-side.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const refresh = async () => {
      // iter_474 — Skip work when tab is in background; the radio +
      // CPU wake-ups every 30s were draining battery / warming phones.
      // We still re-fire on the visibilitychange edge below so the UI
      // doesn't go stale when the user comes back.
      if (document.hidden) return;
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });
        if (alive && res.data) setUser(res.data);
      } catch (_e) { /* silent — keep current session */ }
    };
    const interval = setInterval(refresh, 30000);
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      alive = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token, API]);

  const login = async (email, password, twoFactorCode = null) => {
    const payload = { email, password };
    if (twoFactorCode) {
      payload.two_factor_code = twoFactorCode;
    }
    const response = await axios.post(`${API}/auth/login`, payload, {
      timeout: 30000,
      withCredentials: false
    });
    const data = response.data;
    if (!data || !data.token) {
      throw Object.assign(new Error('Invalid server response'), {
        response: { status: 502, data: { detail: 'Server returned an unexpected response. Please try again.' } }
      });
    }
    const { token: newToken, user: userData, force_password_change } = data;
    
    // Set the force password change flag BEFORE setting token/user
    // This allows proper redirect handling before PublicRoute kicks in
    if (force_password_change) {
      setForcePasswordChange(true);
      secureStorage.setItem('forcePasswordChange', 'true');
    }
    
    secureStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    // Return both user data and force_password_change flag
    return { user: userData, forcePasswordChange: force_password_change || false };
  };

  const register = async (username, email, password, isArtist = false) => {
    const response = await axios.post(`${API}/auth/register`, { 
      username, 
      email, 
      password,
      is_artist: isArtist
    });
    const { token: newToken, user: userData } = response.data;
    secureStorage.setItem('token', newToken);
    secureStorage.setItem('is_first_login', 'true'); // Mark as first login
    setToken(newToken);
    setUser(userData);
    setIsFirstLogin(true);
    return userData;
  };

  const logout = () => {
    secureStorage.removeItem('token');
    secureStorage.removeItem('is_first_login');
    secureStorage.removeItem('forcePasswordChange');
    setToken(null);
    setUser(null);
    setIsFirstLogin(false);
    setForcePasswordChange(false);
  };

  const clearFirstLogin = () => {
    secureStorage.removeItem('is_first_login');
    setIsFirstLogin(false);
  };

  const becomeArtist = async () => {
    await axios.put(`${API}/auth/become-artist`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser({ ...user, is_artist: true });
  };

  const becomeProducer = async () => {
    await axios.put(`${API}/auth/become-producer`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser({ ...user, is_producer: true });
  };

  const becomeAIArtist = async () => {
    await axios.put(`${API}/auth/become-ai-artist`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser({ ...user, is_ai_artist: true });
  };

  const becomeDJ = async () => {
    await axios.put(`${API}/auth/become-dj`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser({ ...user, is_dj: true });
  };

  const becomePromoter = async () => {
    await axios.put(`${API}/auth/become-promoter`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser({ ...user, is_promoter: true });
  };

  const upgradeRole = async (addArtist = false, addProducer = false, addAIArtist = false) => {
    const response = await axios.post(`${API}/auth/upgrade-role`, null, {
      headers: { Authorization: `Bearer ${token}` },
      params: { add_artist: addArtist, add_producer: addProducer, add_ai_artist: addAIArtist }
    });
    await refreshUser();
    return response.data;
  };

  const updateProfilePicture = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/profile/picture`, formData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    setUser({ ...user, profile_picture: response.data.filename });
    return response.data;
  };

  const updateBio = async (bio) => {
    await axios.put(`${API}/profile/bio`, { bio }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser({ ...user, bio });
  };

  const refreshUser = async () => {
    if (token) {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    }
  };

  // Replace the in-memory + persisted token with a freshly-issued one.
  // Used by sign-out-everywhere where the backend rotates active_session_id
  // and hands us back a new token bound to the rotated id.
  const replaceToken = (newToken) => {
    if (!newToken) return;
    secureStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    becomeArtist,
    becomeProducer,
    becomeAIArtist,
    becomeDJ,
    becomePromoter,
    upgradeRole,
    updateProfilePicture,
    updateBio,
    refreshUser,
    replaceToken,
    isAuthenticated: !!user,
    isArtist: user?.is_artist || false,
    isProducer: user?.is_producer || false,
    isAIArtist: user?.is_ai_artist || false,
    isDJ: user?.is_dj || false,
    isPromoter: user?.is_promoter || false,
    termsAccepted: !!(user?.terms_accepted && user?.privacy_accepted),
    isFirstLogin,
    clearFirstLogin,
    forcePasswordChange,
    clearForcePasswordChange: () => {
      setForcePasswordChange(false);
      secureStorage.removeItem('forcePasswordChange');
    },
    BACKEND_URL,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
